import * as React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { activePathIn, NavItem } from './nav-model';
import { cn } from '@/lib/utils';

const CLOSE_DELAY_MS = 160;

// Only one flyout at a time: moving from one row to the next closes the
// previous panel at once instead of letting it linger over the new one
// for its close delay.
let closeOpenFlyout: (() => void) | null = null;

export interface FlyoutRowProps {
    open: boolean;
    rowRef: React.RefObject<HTMLAnchorElement>;
    /** Spread on the row's link: keyboard + touch entry into the flyout. */
    linkProps: {
        'aria-haspopup': 'true';
        'aria-expanded': boolean;
        onKeyDown: (event: React.KeyboardEvent<HTMLAnchorElement>) => void;
        onPointerDown: (event: React.PointerEvent<HTMLAnchorElement>) => void;
        onClick: (event: React.MouseEvent<HTMLAnchorElement>) => void;
    };
}

/**
 * A sidebar row whose related pages open in a flyout beside the sidebar.
 *
 * - Mouse: opens on hover; a short close delay lets the pointer cross the
 *   gap into the panel. Clicking the row still navigates to its page.
 * - Keyboard: the arrow key pointing into the page opens it and focuses the
 *   first link; Up/Down move, Escape or the opposite arrow return.
 * - Touch (no hover): the first tap opens the flyout instead of navigating;
 *   a second tap on the row goes to the row's own page.
 *
 * The panel has no visible heading; the row's label names it for
 * assistive tech instead.
 */
export default function NavFlyout({
    route,
    items,
    pathname,
    side,
    onNavigate,
    children: renderRow,
}: {
    route: NavItem;
    items: NavItem[];
    pathname: string;
    /** Physical side the panel opens on: towards the page. */
    side: 'left' | 'right';
    onNavigate?: () => void;
    children: (props: FlyoutRowProps) => React.ReactNode;
}) {
    const { t } = useTranslation('crm');
    const [open, setOpenState] = React.useState(false);
    const closeSelf = React.useCallback(() => setOpenState(false), []);
    const setOpen = React.useCallback(
        (next: boolean) => {
            if (next) {
                if (closeOpenFlyout && closeOpenFlyout !== closeSelf) {
                    closeOpenFlyout();
                }
                closeOpenFlyout = closeSelf;
            } else if (closeOpenFlyout === closeSelf) {
                closeOpenFlyout = null;
            }
            setOpenState(next);
        },
        [closeSelf]
    );
    const rowRef = React.useRef<HTMLAnchorElement>(null);
    const contentRef = React.useRef<HTMLDivElement>(null);
    const byKeyboard = React.useRef(false);
    const lastPointer = React.useRef<string>('mouse');
    const closeTimer = React.useRef<number>();

    const cancelClose = () => {
        if (closeTimer.current) window.clearTimeout(closeTimer.current);
        closeTimer.current = undefined;
    };
    const scheduleClose = () => {
        cancelClose();
        closeTimer.current = window.setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
    };
    React.useEffect(
        () => () => {
            cancelClose();
            if (closeOpenFlyout === closeSelf) closeOpenFlyout = null;
        },
        [closeSelf]
    );

    const intoPage = side === 'right' ? 'ArrowRight' : 'ArrowLeft';
    const backOut = side === 'right' ? 'ArrowLeft' : 'ArrowRight';

    const links = () =>
        Array.from(contentRef.current?.querySelectorAll<HTMLAnchorElement>('a') ?? []);

    const close = () => {
        setOpen(false);
        if (byKeyboard.current) rowRef.current?.focus();
    };

    const activeChild = activePathIn(
        items.map(i => i.path),
        pathname
    );

    const linkProps: FlyoutRowProps['linkProps'] = {
        'aria-haspopup': 'true',
        'aria-expanded': open,
        onKeyDown: event => {
            if (event.key === intoPage || (open && event.key === 'ArrowDown')) {
                event.preventDefault();
                byKeyboard.current = true;
                if (open) links()[0]?.focus();
                else setOpen(true);
            }
        },
        onPointerDown: event => {
            lastPointer.current = event.pointerType;
        },
        onClick: event => {
            if (lastPointer.current !== 'mouse' && !open) {
                event.preventDefault();
                byKeyboard.current = false;
                setOpen(true);
            }
        },
    };

    const linkClass = (active: boolean) =>
        cn(
            'flex h-8 items-center rounded-md px-2.5 text-[13px] font-medium no-underline outline-none transition-colors duration-100 focus-visible:ring-2 focus-visible:ring-ring',
            active
                ? 'bg-primary/10 text-primary-on-soft'
                : 'text-fg-secondary hover:bg-surface-hover hover:text-foreground'
        );

    return (
        <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
            <PopoverPrimitive.Anchor asChild>
                <div
                    onPointerEnter={event => {
                        if (event.pointerType !== 'mouse') return;
                        cancelClose();
                        byKeyboard.current = false;
                        setOpen(true);
                    }}
                    onPointerLeave={event => {
                        if (event.pointerType === 'mouse') scheduleClose();
                    }}
                >
                    {renderRow({ open, rowRef, linkProps })}
                </div>
            </PopoverPrimitive.Anchor>
            <PopoverPrimitive.Portal>
                <PopoverPrimitive.Content
                    ref={contentRef}
                    aria-label={t(route.labelKey, route.label)}
                    side={side}
                    align="start"
                    sideOffset={14}
                    alignOffset={-6}
                    collisionPadding={8}
                    onOpenAutoFocus={event => {
                        event.preventDefault();
                        if (byKeyboard.current) links()[0]?.focus();
                    }}
                    onCloseAutoFocus={event => event.preventDefault()}
                    onEscapeKeyDown={event => {
                        event.preventDefault();
                        close();
                    }}
                    onPointerEnter={cancelClose}
                    onPointerLeave={event => {
                        if (event.pointerType === 'mouse') scheduleClose();
                    }}
                    onKeyDown={event => {
                        const list = links();
                        const index = list.indexOf(document.activeElement as HTMLAnchorElement);
                        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                            event.preventDefault();
                            const step = event.key === 'ArrowDown' ? 1 : -1;
                            list[(index + step + list.length) % list.length]?.focus();
                        } else if (event.key === backOut) {
                            event.preventDefault();
                            close();
                        }
                    }}
                    className="z-dropdown min-w-[13rem] rounded-lg border border-border bg-popover p-1.5 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
                >
                    <ul className="m-0 list-none space-y-0.5 p-0">
                        {items.map(item => {
                            const active = item.path === activeChild;
                            return (
                                <li key={item.path}>
                                    <Link
                                        to={item.path}
                                        aria-current={active ? 'page' : undefined}
                                        onClick={() => {
                                            setOpen(false);
                                            onNavigate?.();
                                        }}
                                        className={linkClass(active)}
                                    >
                                        {t(item.labelKey, item.label)}
                                    </Link>
                                </li>
                            );
                        })}
                    </ul>
                </PopoverPrimitive.Content>
            </PopoverPrimitive.Portal>
        </PopoverPrimitive.Root>
    );
}
